import { type ComponentProps, type ForwardRefExoticComponent, type RefAttributes, useMemo } from 'react'

import { Label } from '@/components/ui/label.tsx'

import { StoryLabel } from '@/components/form/StoryLabel.tsx'

import { EditableComponent } from '@/components/EditableComponent.tsx'
import { MultiSelectCombobox } from '@/components/MultiSelectCombobox.tsx'
import { useMutateStoryLabels } from '@/requests/mutations/useMutateStoryLabels.ts'
import { useQueryLabels } from '@/requests/queries/useQueryProject.ts'
import { isTemporaryId } from '@/utils/key.ts'

type Props = {
  storyId: string
  value?: Label[]
}

export const EditableStoryLabels = ({ storyId, value = [] }: Props) => {
  const { labels } = useQueryLabels()
  const { add, remove } = useMutateStoryLabels(storyId)

  const addLabel = async (item: ComboboxItem) =>
    add({
      id: item.id,
      name: item.name,
      count: labels.find((label) => label.id === item.id)?.count ?? 0,
    })

  const removeLabel = async (item: ComboboxItem) => remove(item.id)

  const selectableItems = useMemo(() => {
    const seen = new Map()
    return [...labels, ...value.filter((v) => isTemporaryId(v.id))]
      .filter((item) => {
        if (seen.has(item.id)) return false

        seen.set(item.id, true)
        return true
      })
      .map(toComboboxItem)
  }, [labels, value])

  return (
    <div className="flex flex-col gap-2">
      <Label variant="title">Labels</Label>
      <div className="flex w-full">
        <MultiSelectCombobox
          items={selectableItems}
          selectedItems={value.map(toComboboxItem)}
          SelectedItemComponent={StoryLabel}
          SearchResultItemComponent={StoryLabel}
          onSelect={addLabel}
          onRemove={removeLabel}
          placeholder="Add a label"
        />
      </div>
    </div>
  )
}

export const TypedEditableStoryLabels = EditableComponent as unknown as ForwardRefExoticComponent<
  EditableProps<Label[], undefined, ComponentProps<typeof EditableStoryLabels>> & RefAttributes<EditableCommands>
>

const toComboboxItem = (label: Label): ComboboxItem => ({ name: label.name, id: label.id })
